import { Alert, Anchor, Box, Button, MultiSelect, PasswordInput, Stack, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconBriefcase,
  IconCoin,
  IconShieldCheck,
  IconTrendingUp,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppleJoinButton } from "../../components/AppleJoinButton";
import { AuthIdentityChooser } from "../../components/AuthIdentityChooser";
import { CitySelect } from "../../components/CitySelect";
import { GoogleJoinButton } from "../../components/GoogleJoinButton";
import { OnboardingSection, OnboardingShell } from "../../components/OnboardingShell";
import { AUTH_IDENTITY_COPY } from "../../lib/kyc";
import { PhoneInput } from "../../components/PhoneInput";
import { RecruiterTermsCard } from "../../components/RecruiterTermsCard";
import { TERMS_VERSION } from "../../constants/platformTerms";
import { useAuth } from "../../context/useAuth";
import { authApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { clearSignupSession, getSignupSession, hasSignupSession } from "../../lib/googleSignupSession";
import { catalogFuzzyFilter } from "../../lib/catalogFuzzyFilter";
import { usePublicSegmentCatalog } from "../../lib/useSegmentCatalog";
import { renderSegmentOption } from "../../components/SegmentOptionLabel";
import { validateIndianPhoneField, toPhoneForApi } from "../../lib/phone";
import { recruiterSignupStep } from "../../lib/recruiterOnboarding";
import { SEGMENT_SELECT_COPY } from "../../lib/segments";
import { CITY_SELECT_COPY } from "../../lib/cities";
import {
  REGISTRATION_PASSWORD_COPY,
  validatePasswordConfirmation,
} from "../../constants/account";
import type { Segment } from "../../types/api";
import "../../components/authAnimations.css";
import { axioTokens } from "../../theme";

export function JoinRecruiterPage() {
  const { register, applyTokenUser } = useAuth();
  const navigate = useNavigate();
  const { flatOptions: segmentOptions } = usePublicSegmentCatalog();
  const [googleConnected, setGoogleConnected] = useState(hasSignupSession());
  const [masterAccepted, setMasterAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [termsVersion, setTermsVersion] = useState(TERMS_VERSION);
  const [segments, setSegments] = useState<string[]>([]);
  const [segmentsError, setSegmentsError] = useState<string | null>(null);

  const emailForm = useForm({
    initialValues: {
      full_name: "",
      email: "",
      phone: "",
      password: "",
      confirm_password: "",
      city: "",
    },
    validate: {
      full_name: (v) => (v.trim().length >= 2 ? null : "Enter your full name"),
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : "Enter a valid email"),
      phone: validateIndianPhoneField,
      password: (v) => (v.length >= 8 ? null : "Password must be at least 8 characters"),
      confirm_password: (v, values) => validatePasswordConfirmation(values.password, v),
      city: (v) => (v.trim() ? null : CITY_SELECT_COPY.requiredError),
    },
  });

  useEffect(() => {
    authApi.joinTerms("recruiter").then((res) => setTermsVersion(res.version)).catch(() => {});
  }, []);

  const accountReady = googleConnected || emailFormOpen;
  const activeStep = recruiterSignupStep(accountReady, segments.length, masterAccepted);
  const termsOk = masterAccepted;
  const cityOk = Boolean(emailForm.values.city.trim());
  const canSubmitGoogle = googleConnected && termsOk && segments.length > 0 && cityOk;

  async function submitGoogleAccess() {
    const session = getSignupSession();
    if (!session) {
      notifications.show({
        color: "yellow",
        title: "Connect Apple or Google first",
        message: "Use Continue with Apple or Continue with Google above before submitting.",
      });
      return;
    }
    if (segments.length === 0) {
      setSegmentsError(SEGMENT_SELECT_COPY.requiredError);
      return;
    }
    if (!cityOk) {
      emailForm.setFieldError("city", CITY_SELECT_COPY.requiredError);
      return;
    }
    setSubmitting(true);
    setTokenError(null);
    try {
      const completePayload = {
        requested_role: "recruiter" as const,
        city: emailForm.values.city.trim(),
        segments: segments as Segment[],
        terms_version: termsVersion,
        terms_accepted: true,
      };
      const res =
        session.provider === "apple"
          ? await authApi.appleComplete({ ...completePayload, apple_signup_token: session.token })
          : await authApi.googleComplete({ ...completePayload, google_signup_token: session.token });
      clearSignupSession();
      if (res.access_token && res.user) {
        applyTokenUser(res.access_token, res.refresh_token ?? "", res.user);
        notifications.show({
          color: "green",
          title: "Welcome to AxioHire!",
          message: "Complete KYC next — you can browse jobs while you wait for approval.",
        });
        navigate("/kyc");
        return;
      }
      notifications.show({
        color: "red",
        title: "Could not register",
        message: session.provider === "apple" ? AUTH_IDENTITY_COPY.appleSignupFailed : AUTH_IDENTITY_COPY.googleSignupFailed,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "invalid_signup_token") {
          setTokenError(err.message);
          clearSignupSession();
          setGoogleConnected(false);
        } else {
          notifications.show({ color: "red", title: "Failed", message: err.message });
        }
      } else {
        notifications.show({ color: "red", title: "Failed", message: "Could not submit request" });
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function submitEmailRegister(values: typeof emailForm.values) {
    if (!termsOk || segments.length === 0 || !values.city.trim()) {
      notifications.show({ color: "yellow", title: "Accept terms", message: "Please accept the platform terms." });
      return;
    }
    setEmailSubmitting(true);
    try {
      await register({
        email: values.email,
        password: values.password,
        full_name: values.full_name,
        phone: toPhoneForApi(values.phone),
        city: values.city.trim(),
        segments: segments as Segment[],
        terms_version: termsVersion,
        terms_accepted: true,
      });
      notifications.show({
        color: "green",
        title: "Welcome to AxioHire!",
        message: "Complete KYC next — you can browse jobs while you wait for approval.",
      });
      navigate("/kyc");
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Registration failed", message });
    } finally {
      setEmailSubmitting(false);
    }
  }

  return (
    <OnboardingShell
      role="recruiter"
      accent="violet"
      activeStep={activeStep}
      steps={["Create account", "Choose segments", "Accept terms", "Continue to KYC"]}
      heroTitle={
        <>
          Earn on every
          <br />
          <span className="gradientText">placement you make</span>
        </>
      }
      heroSubtitle="Source, screen, and submit candidates for Voice BPO, Chat Support, IT Helpdesk, and more — with transparent pipeline tracking."
      statLabel="Recruiter share"
      statValue="Up to 80%"
      highlights={[
        { icon: IconCoin, text: "70–80% of placement fee on confirmed joins" },
        { icon: IconBriefcase, text: "High-volume roles from verified companies" },
        { icon: IconTrendingUp, text: "Track every candidate through hiring stages" },
        { icon: IconShieldCheck, text: "KYC verification unlocks full payouts" },
      ]}
      footer={
        <>
          Not a recruiter?{" "}
          <Anchor component={Link} to="/join" fw="var(--axio-font-weight-semibold)" c="violet.7">
            Choose a different path
          </Anchor>
        </>
      }
    >
      <Stack gap="xl">
        <div className="staggerEnter" style={{ animationDelay: "var(--axio-duration-quick)" }}>
          <Title order={3} fw="var(--axio-font-weight-extra-bold)" style={{ fontFamily: "var(--axio-font-head)" }}>
            Recruiter onboarding
          </Title>
          <Text size="sm" c="dimmed" mt="var(--axio-unit-8)">
            Create your account in under a minute. Contact verification, resume, and identity documents follow in
            one guided KYC flow.
          </Text>
        </div>

        {tokenError && (
          <Box className="staggerEnter" style={{ animationDelay: "var(--axio-duration-fast)" }}>
            <Alert color="red" variant="light" title="Session expired">
              {tokenError}
              <Text size="sm" mt="var(--axio-unit-4)">
                Click Continue with Apple or Continue with Google again to restart.
              </Text>
            </Alert>
          </Box>
        )}

        <Box className="staggerEnter" style={{ animationDelay: "var(--axio-duration-standard)" }}>
          <OnboardingSection
            title={AUTH_IDENTITY_COPY.sectionTitle}
            description={AUTH_IDENTITY_COPY.joinDescription}
            accent="violet"
          >
            {googleConnected ? (
              <Alert
                color="violet"
                variant="light"
                title={
                  getSignupSession()?.provider === "apple"
                    ? AUTH_IDENTITY_COPY.appleConnectedTitle
                    : AUTH_IDENTITY_COPY.googleConnectedTitle
                }
                icon={<IconShieldCheck size="var(--axio-unit-18)" />}
                styles={{
                    root: {
                        background: axioTokens.violetAlpha06,
                        borderColor: axioTokens.violetAlpha15,
                    }
                }}
              >
                {getSignupSession()?.provider === "apple"
                  ? AUTH_IDENTITY_COPY.appleConnectedBody
                  : AUTH_IDENTITY_COPY.googleConnectedBody}
              </Alert>
            ) : (
              <AuthIdentityChooser
                appleSlot={<AppleJoinButton returnPath="/join/recruiter" />}
                googleSlot={
                  <GoogleJoinButton
                    onConnected={() => {
                      setGoogleConnected(true);
                      setEmailFormOpen(false);
                    }}
                  />
                }
                emailOpen={emailFormOpen}
                onChooseEmail={() => setEmailFormOpen(true)}
                emailFields={
                  <form onSubmit={(event) => event.preventDefault()}>
                    <Stack gap="md">
                      <Box className="glowInput">
                        <TextInput label="Full name" size="md" required {...emailForm.getInputProps("full_name")} styles={{ input: { transition: "var(--axio-transition-input)" } }} />
                      </Box>
                      <Box className="glowInput">
                        <TextInput label="Email" size="md" required {...emailForm.getInputProps("email")} styles={{ input: { transition: "var(--axio-transition-input)" } }} />
                      </Box>
                      <Box className="glowInput">
                        <PhoneInput
                          label="Phone"
                          size="md"
                          required
                          value={emailForm.values.phone}
                          onChange={(value) => emailForm.setFieldValue("phone", value)}
                          error={emailForm.errors.phone}
                        />
                      </Box>
                      <Box className="glowInput">
                        <PasswordInput
                          label={REGISTRATION_PASSWORD_COPY.passwordLabel}
                          size="md"
                          required
                          {...emailForm.getInputProps("password")}
                          styles={{
                            input: { transition: "var(--axio-transition-input)" },
                            visibilityToggle: { display: "none" },
                          }}
                        />
                      </Box>
                      <Box className="glowInput">
                        <PasswordInput
                          label={REGISTRATION_PASSWORD_COPY.confirmPasswordLabel}
                          size="md"
                          required
                          {...emailForm.getInputProps("confirm_password")}
                          styles={{ input: { transition: "var(--axio-transition-input)" } }}
                        />
                      </Box>
                    </Stack>
                  </form>
                }
              />
            )}
          </OnboardingSection>
        </Box>

        {accountReady && (
          <Box className="staggerEnter" style={{ animationDelay: "var(--axio-duration-stagger5)" }}>
            <OnboardingSection
              title={SEGMENT_SELECT_COPY.recruitForLabel}
              description={SEGMENT_SELECT_COPY.recruitForDescription}
              accent="violet"
            >
              <Stack gap="md">
                <CitySelect
                  label={CITY_SELECT_COPY.label}
                  publicCatalog
                  withAsterisk
                  clearable={false}
                  value={emailForm.values.city}
                  onChange={(city) => emailForm.setFieldValue("city", city)}
                  error={emailForm.errors.city}
                />
                <Box className="glowInput">
                  <MultiSelect
                    label={SEGMENT_SELECT_COPY.recruitForLabel}
                    withAsterisk
                    size="md"
                    searchable
                    clearable
                    data={segmentOptions}
                    renderOption={renderSegmentOption}
                    filter={catalogFuzzyFilter}
                    limit={40}
                    nothingFoundMessage={SEGMENT_SELECT_COPY.nothingFound}
                    value={segments}
                    onChange={(value) => {
                      setSegments(value);
                      setSegmentsError(value.length === 0 ? SEGMENT_SELECT_COPY.requiredError : null);
                    }}
                    error={segmentsError}
                    styles={{
                      input: {
                        transition: "var(--axio-transition-input)",
                      },
                    }}
                  />
                </Box>
              </Stack>
            </OnboardingSection>
          </Box>
        )}

        {accountReady && (
          <OnboardingSection
            title="Platform terms"
            description="Review the recruiter agreement, then confirm your acceptance."
            accent="violet"
          >
            <RecruiterTermsCard
              accepted={masterAccepted}
              onAcceptedChange={setMasterAccepted}
              version={termsVersion}
            />
          </OnboardingSection>
        )}

        {accountReady && (
          <Stack gap="xs">
            <Button
              className="ctaGlow"
              color="violet"
              size="md"
              fullWidth
              loading={googleConnected ? submitting : emailSubmitting}
              disabled={googleConnected ? !canSubmitGoogle : !termsOk || segments.length === 0 || !cityOk}
              onClick={() => {
                if (googleConnected) {
                  void submitGoogleAccess();
                } else {
                  const result = emailForm.validate();
                  if (!result.hasErrors) void submitEmailRegister(emailForm.values);
                }
              }}

              style={{ height: "var(--axio-unit-48)", fontSize: "var(--axio-font-size-control)", fontWeight: "var(--axio-font-weight-semibold)" }}
            >
              Create account & continue to KYC
            </Button>
            <Text ta="center" size="xs" c="dimmed">
              Next: verify your contact details, upload your resume, and complete KYC.
            </Text>
          </Stack>
        )}
      </Stack>
    </OnboardingShell>
  );
}
